import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { moduleMeta, wsFetch, todayIST } from "./theme";
import { BiPageHeader } from "./BiPageHeader";
import { StatusTiles } from "./StatusTiles";
import { BookingTimeline, AttentionList } from "./BookingTimeline";
import { TableGrid } from "./TableGrid";
import { TimeSlots, BookingRows } from "./BookingRows";
import { QuickTableBooking } from "./QuickTableBooking";
import { ReminderWorkspace } from "./ReminderWorkspace";
import { StageFlow, ActionCards } from "./StageFlow";
import { ChevronDown, ChevronUp } from "lucide-react";

const FLOW_TABS = new Set(["pickup", "catering", "event", "tiffin", "ganesh-modak", "corporate-invoices", "gift-vouchers"]);
const API = process.env.REACT_APP_BACKEND_URL;

const NO_TILES = new Set(["live-queue", "overlap", "history"]);

export function BiWorkspace({ tab, session, centers, center, setCenter, onOpen, actions, constants, onEditBooking, onWhatsAppBooking, refreshKey }) {
  const token = session?.token;
  const multi = !!(session?.is_super_admin || session?.is_admin);
  const meta = moduleMeta(tab);
  const [date, setDate] = useState(todayIST());
  const [slot, setSlot] = useState("");
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [flow, setFlow] = useState(null);
  const [open, setOpen] = useState(() => localStorage.getItem("bi_dash_open") !== "0");
  const toggle = () => { localStorage.setItem("bi_dash_open", open ? "0" : "1"); setOpen(!open); };
  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    if (NO_TILES.has(tab)) { setData(null); return; }
    wsFetch(token, { module: tab, center, date, slot: tab === "bookings" ? slot : "" })
      .then((d) => { if (id === reqId.current) setData(d); })
      .catch((e) => { if (id === reqId.current) toast.error(e.message); });
  }, [token, tab, center, date, slot]);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener("bi-refresh", h);
    return () => window.removeEventListener("bi-refresh", h);
  }, []);
  useEffect(() => { load(); }, [load, tick, refreshKey]);
  useEffect(() => {
    if (!FLOW_TABS.has(tab)) { setFlow(null); return; }
    let live = true;
    fetch(`${API}/api/booking-workspace/flow`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, module: tab, center, date }) })
      .then((r) => r.json()).then((d) => { if (live) setFlow(d); }).catch(() => {});
    return () => { live = false; };
  }, [token, tab, center, date, tick, refreshKey]);
  useEffect(() => { setSlot(""); }, [tab]);
  const live = (centers || []).filter((c) => /^PB-/.test(c.code) && !/^PB-(Q|DELETE)/.test(c.code));
  const centreName = center ? (centers || []).find((c) => c.code === center)?.name || center : multi ? "All centres" : session?.center;
  const refresh = () => setTick((t) => t + 1);
  return (
    <div className="bi-ws space-y-4" data-testid={`bi-ws-${tab}`}>
      <BiPageHeader meta={meta} tab={tab} centreName={centreName} multi={multi} centers={live} center={center} setCenter={setCenter}
        date={date} setDate={setDate} onBack={() => onOpen("home")} onOpen={onOpen} actions={actions} />
      {!NO_TILES.has(tab) && (
        <button type="button" onClick={toggle} className="bi-pill" data-testid="bi-dash-toggle">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}{open ? "Hide dashboard" : "Show dashboard"}
        </button>)}
      {open && data && <StatusTiles tiles={data.tiles} fill={meta.fill} light={meta.light} />}
      {open && flow?.flow && <StageFlow title={meta.title} steps={flow.flow} accent={meta.accent} />}
      {open && flow?.cards && <ActionCards cards={flow.cards} onShowRecords={() => document.querySelector(".bi-module")?.scrollIntoView({ behavior: "smooth" })} />}
      {data && tab === "bookings" && (<>
        <TimeSlots slots={data.slots} active={slot} onPick={setSlot} />
        <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <QuickTableBooking token={token} center={center || (multi ? "" : session?.center)} date={date} constants={constants} onCreated={refresh} />
          <TableGrid tables={data.tables} showCentre={!center} title={slot ? `Tables · ${slot}` : "Tables now"} token={token} onChanged={refresh} onOpen={onOpen} />
        </div>
        <BookingRows rows={data.bookings} slot={slot} onEdit={onEditBooking} onWhatsApp={onWhatsAppBooking} />
      </>)}
      {data && tab === "tables" && <TableGrid tables={data.tables} showCentre={!center} title="Floor" token={token} onChanged={refresh} onOpen={onOpen} />}
      {data && tab === "reminders" && <ReminderWorkspace token={token} center={center} onChanged={refresh} />}
      {data && tab === "dashboard" && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
          <BookingTimeline items={data.timeline} onOpen={onOpen} />
          <AttentionList items={data.attention} onOpen={onOpen} />
        </div>)}
    </div>
  );
}
